import * as React from 'react';
import styled, { createGlobalStyle, css, useTheme } from 'styled-components';

import { color, media, theme, variants } from 'styles/utils';

import useMatchMedia from 'hooks/useMatchMedia';
import useResizeObserver from 'hooks/useResizeObserver';
import useIntersectionObserver from 'hooks/useIntersectionObserver';

import Button from 'components/Button';
import Container from 'components/Container';

import NavMenu from './NavMenu';

const StyledNavbar = styled.div`
  font-size: 0.875rem;
  overscroll-behavior: none;
  position: relative;
  z-index: ${theme('zIndex.banner')};

  &[data-expanded='true'],
  &[data-transitioning] {
    z-index: calc(${theme('zIndex.banner')} + 1);
  }

  @media (min-height: 24em) {
    &[data-sticky] {
      position: sticky;
      top: 0;
    }
  }

  &[data-inset] {
    margin-bottom: calc(0px - var(--sleuth-safe-area-inset-top, 0));
  }

  ${variants({
    primary: css`
      background: ${color('mediumTeal')};
      color: ${color('white')};
      padding: 1.5rem 0;
      transition: background 200ms cubic-bezier(0.5, 0, 0.5, 1);

      &[data-inset][data-static]:not([data-expanded='true']) {
        background: transparent;
      }

      /* Hacky fix to make popup sub menus render above secondary navbars */
      z-index: calc(${theme('zIndex.banner')} + 1);
    `,
    secondary: css`
      background: ${color('white')};
      padding: 1rem 0;
      transition: border-radius 200ms cubic-bezier(0.5, 0, 0.5, 1);

      &,
      &[data-expanded='true'] nav {
        border-bottom-left-radius: 1rem;
        border-bottom-right-radius: 1rem;
        box-shadow: 0 2px 3px #0003, 0 4px 16px -8px #0002;
      }

      &[data-expanded='true'] nav {
        margin-bottom: 1rem;
        padding-bottom: 0.25rem;
      }

      &[data-expanded='true'] {
        border-radius: 0;
        box-shadow: none;
      }
    `,
  })}
`;

const StyledContainer = styled(Container)`
  position: revert;
  align-items: center;
  display: grid;
  grid-template-areas: 'brand toggle' 'content content';
  grid-template-columns: repeat(2, minmax(0, max-content));
  justify-content: space-between;

  ${media.min('nav')} {
    grid-template-areas: 'brand content';
    grid-template-columns: minmax(max-content, 14rem) minmax(0, 1fr);
  }
`;

const StyledNavbarToggle = styled(Button)`
  grid-area: toggle;
  justify-self: end;
  padding: 0.5rem 1rem;

  ${media.min('nav')} {
    display: none;
  }
`;

const StyledNavbarBrand = styled.div`
  grid-area: brand;
  padding-left: 1rem;
  padding-right: 1rem;
`;

const StyledNavbarMenuContainer = styled.div`
  grid-area: content;
  display: grid;
  grid-template-rows: 1fr;
  overscroll-behavior: none;

  & > * {
    min-height: 0;
  }

  ${media.max('nav')} {
    left: 0;
    overflow: hidden;
    position: absolute;
    top: var(--offset-top, 0);
    transition: none 200ms cubic-bezier(0.5, 0, 0.5, 1);
    transition-property: opacity, grid-template-rows;
    width: 100%;

    [data-expanded='false'] & {
      opacity: 0;
    }

    [data-expanded='false']:not([data-modal]) & {
      grid-template-rows: 0fr;
    }

    [data-modal] & {
      transition-property: opacity, height;
    }

    [data-modal][data-expanded='false'] & {
      height: 0;
    }

    [data-modal][data-expanded='true'] & {
      height: calc(100vh - var(--offset-top, 0));
    }

    [data-expanded='false']:not([data-transitioning]) & > * {
      height: 0;
      visibility: hidden;
    }
  }
`;

const Style = createGlobalStyle`
  :root { --sleuth-safe-area-inset-top: ${(p) => p.insetLength}; }
`;

const NavbarContext = React.createContext({});

export default function Navbar({
  children,
  inset,
  sticky,
  modal,
  id,
  ...props
}) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const [isStatic, setIsStatic] = React.useState(true);
  const [height, setHeight] = React.useState(inset ? '90px' : undefined);

  const { breakpoints } = useTheme();
  const isCollapsible = useMatchMedia(`(max-width: ${breakpoints.nav})`, true);

  const ref = useResizeObserver(
    React.useCallback(
      ({ borderBoxSize }) => {
        if (borderBoxSize?.length > 0) {
          const [{ blockSize }] = borderBoxSize;
          setHeight(`${blockSize}px`);
          if (inset) {
            window.sleuthSafeAreaInsetTop = blockSize;
          }
        }
      },
      [inset, setHeight]
    )
  );

  const intersectionPlaceholder = useIntersectionObserver(
    React.useCallback(
      (entry) => {
        setIsStatic(entry.isIntersecting);
      },
      [setIsStatic]
    ),
    React.useMemo(() => ({ rootMargin: '0px 0px 0px' }), [])
  );

  React.useEffect(() => {
    if (!isCollapsible) {
      setIsExpanded(false);
    }
  }, [isCollapsible, setIsExpanded]);

  React.useEffect(() => {
    const lockScroll = getScrollLock();
    let unlockScroll;
    if (modal && isExpanded && !isStatic) {
      unlockScroll = lockScroll(ref.current);
    }
    return () => {
      unlockScroll?.();
    };
  }, [isExpanded, isStatic, modal, ref]);

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (isExpanded && ref.current && !ref.current.contains(event.target)) {
        setIsExpanded(false);
        setIsTransitioning(true);
      }
    }
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isExpanded, ref, setIsExpanded]);

  const context = {
    getToggleProps: (rest = {}) => ({
      ...rest,
      onClick: () => {
        setIsExpanded((prev) => !prev);
        setIsTransitioning(true);
        if (!isExpanded) {
          ref.current?.scrollIntoView(true);
        }
      },
    }),
    getContentProps: (rest = {}) => ({
      ...rest,
      id,
      onTransitionEnd: (event) => {
        if (
          event.propertyName === 'grid-template-rows' ||
          event.propertyName === 'height'
        ) {
          setIsTransitioning(false);
        }
      },
      style: { '--offset-top': height },
    }),
    isExpanded,
  };

  return (
    <>
      <div aria-hidden="true" ref={intersectionPlaceholder} />
      {inset && <Style insetLength={height || '0px'} />}
      <NavbarContext.Provider value={context}>
        <StyledNavbar
          data-expanded={isCollapsible ? isExpanded : undefined}
          data-inset={inset || undefined}
          data-modal={(isCollapsible && modal) || undefined}
          data-static={isStatic || !sticky || undefined}
          data-sticky={sticky || undefined}
          data-transitioning={isTransitioning || undefined}
          ref={ref}
          {...props}
        >
          <StyledContainer>{children}</StyledContainer>
        </StyledNavbar>
      </NavbarContext.Provider>
    </>
  );
}

Navbar.defaultProps = {
  modal: false,
};

Object.assign(Navbar, {
  Brand: NavbarBrand,
  Menu: NavbarMenu,
  Toggle: NavbarToggle,
});

function NavbarBrand({ children, ...props }) {
  return <StyledNavbarBrand {...props}>{children}</StyledNavbarBrand>;
}

function NavbarMenu({ menu, ...props }) {
  const { getContentProps } = React.useContext(NavbarContext);
  const { variant, ...rest } = getContentProps(props);
  return (
    <StyledNavbarMenuContainer {...rest} variant={variant}>
      <NavMenu {...menu} variant={variant} />
    </StyledNavbarMenuContainer>
  );
}

function NavbarToggle({ children, ...props }) {
  const { getToggleProps, isExpanded } = React.useContext(NavbarContext);
  return (
    <StyledNavbarToggle
      {...getToggleProps({
        size: null,
        type: 'button',
        variant: null,
        ...props,
      })}
    >
      {typeof children === 'function' ? children({ isExpanded }) : children}
    </StyledNavbarToggle>
  );
}

function getScrollLock() {
  return (ref) => {
    const activeElement = document.hasFocus() && document.activeElement;
    const modal = ref.current || ref;
    let siblings = [];
    let el = modal;

    while (
      el instanceof HTMLElement &&
      el.parentElement &&
      document.body.contains(el) &&
      el !== document.body
    ) {
      const children = Array.from(el.parentElement.children);
      siblings = siblings.concat(
        children.filter(
          (child) =>
            !child.contains(modal) &&
            !child.inert &&
            !/(LINK|META|SCRIPT|STYLE)/i.test(child.tagName)
        )
      );
      el = el.parentElement;
    }

    siblings.forEach((sibling) => {
      sibling.inert = true;
    });

    document.body.style.overflow = 'hidden';
    document.body.style.height = '100vh';

    return () => {
      siblings.forEach((sibling) => {
        if (sibling) {
          sibling.inert = false;
        }
      });

      document.body.style.overflow = null;
      document.body.style.height = null;

      requestAnimationFrame(() => {
        if (activeElement) {
          activeElement.focus();
        }
      });
    };
  };
}
